import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { API } from '../config/apiBase';

const STORAGE_KEY_ONCE = 'sv_promo_popup_shown';
const STORAGE_KEY_SESSION = 'sv_promo_popup_session_shown';

function resolveImg(url) {
    if (!url) return '';
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    const base = (API || '').replace(/\/api\/?$/, '');
    return `${base}${url}`;
}

export default function PromoPopup() {
    const [config, setConfig] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        fetch(`${API}/promo-popup`)
            .then((r) => r.json())
            .then((c) => setConfig(c))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!config || !config.enabled) return;

        // Controle de frequência
        if (config.show_frequency === 'once' && localStorage.getItem(STORAGE_KEY_ONCE)) return;
        if (config.show_frequency === 'session' && sessionStorage.getItem(STORAGE_KEY_SESSION)) return;

        const delay = Math.max(0, Number(config.show_after_seconds || 3)) * 1000;
        const t = setTimeout(() => setOpen(true), delay);
        return () => clearTimeout(t);
    }, [config]);

    const close = () => {
        setOpen(false);
        if (!config) return;
        if (config.show_frequency === 'once') localStorage.setItem(STORAGE_KEY_ONCE, '1');
        if (config.show_frequency === 'session') sessionStorage.setItem(STORAGE_KEY_SESSION, '1');
    };

    if (!open || !config) return null;

    const {
        title,
        subtitle,
        cta_text,
        cta_link,
        image_url,
        hero_title,
        hero_subtitle,
        bg_color,
        text_color,
        cta_bg,
        cta_color,
        show_close_button,
    } = config;

    return (
        <div
            role="dialog"
            aria-modal="true"
            data-testid="promo-popup"
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ background: 'rgba(0, 0, 0, 0.72)' }}
            onClick={close}
        >
            <div
                className="relative rounded-2xl overflow-hidden shadow-2xl w-full max-w-3xl"
                style={{ background: '#0a0f1a' }}
                onClick={(e) => e.stopPropagation()}
            >
                {show_close_button && (
                    <button
                        onClick={close}
                        aria-label="Fechar"
                        data-testid="promo-popup-close"
                        className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                        style={{ background: 'rgba(255,255,255,0.9)', color: '#000' }}
                    >
                        <X size={18} strokeWidth={2.2} />
                    </button>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2">
                    {/* Painel colorido (esquerda) - azul Super Vitrine por padrão */}
                    <div
                        className="p-8 md:p-10 flex flex-col justify-center"
                        style={{ background: bg_color || '#1756B8', color: text_color || '#FFFFFF' }}
                    >
                        {hero_title && (
                            <h2 className="text-2xl md:text-3xl font-bold leading-tight mb-2">
                                {hero_title}
                            </h2>
                        )}
                        {hero_subtitle && (
                            <p className="text-sm md:text-base opacity-90">{hero_subtitle}</p>
                        )}
                        {image_url && (
                            <img
                                src={resolveImg(image_url)}
                                alt=""
                                className="mt-5 max-h-56 w-auto object-contain self-center"
                                loading="lazy"
                            />
                        )}
                    </div>

                    {/* Conteúdo + CTA (direita) */}
                    <div
                        className="p-8 md:p-10 flex flex-col justify-center"
                        style={{ background: '#0a0f1a', color: '#fff' }}
                    >
                        {title && <h3 className="text-xl md:text-2xl font-bold mb-2">{title}</h3>}
                        {subtitle && (
                            <p className="text-sm md:text-base opacity-80 mb-6">{subtitle}</p>
                        )}
                        {cta_link && cta_text && (
                            <a
                                href={cta_link}
                                target="_blank"
                                rel="noopener noreferrer sponsored"
                                onClick={close}
                                data-testid="promo-popup-cta"
                                className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold transition-transform hover:scale-[1.02]"
                                style={{
                                    background: cta_bg || '#3BEA86',
                                    color: cta_color || '#03230F',
                                }}
                            >
                                {cta_text}
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
